import {
    Avatar,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
    Menu,
    MenuItem, Box, Chip
} from "@mui/material";
import {getBgColorIcon, getConversationIcon} from "../../../../utils/functions";
import {grey, green} from "@mui/material/colors";
import moment from "moment";
import React, {useContext, useEffect, useState} from "react";
import {HugeiconsIcon} from "@hugeicons/react";
import {CheckCheckIcon, CircleCheckIcon, EyeOffIcon, Message02Icon, PhoneCallIcon, PhoneIcon, Refresh01Icon, Robot01Icon, ViewIcon} from "@hugeicons/core-free-icons";
import {ConversationRepository} from "../../../../repositories/ConversationRepository";
import {toast} from "react-toastify";
import {SignInContext} from "../../../../context/SignInContext";
import useCallConfirm from "../../../../hooks/useCallConfirm";
import {useNavigate, useParams} from "react-router-dom";
import useConfirm from "../../../../hooks/useConfirm";

export const SupervisionChatListItem = React.memo(({
                                                       conversation,
                                                       project,
                                                       onUpdateConversation
                                                   }) => {
    const {conversationId} = useParams()
    const {currentUser} = useContext(SignInContext);
    const [duration, setDuration] = useState("");
    const [menuAnchor, setMenuAnchor] = useState(null);
    const navigate = useNavigate();
    const confirm = useCallConfirm(
        'Do you want to initiate conversation',
    );
    const isResolved = conversation?.resolved && conversation.status !== "SUCCESS";
    const successTag = project?.settings?.tags?.find(tag => tag?.id === project?.configuration?.success_tag) || null;

    useEffect(() => {
        if (conversation?.status !== "ACTIVE" || !conversation?.dateCreated) {
            setDuration("");
            return;
        }

        const updateDuration = () => {
            const start = moment(conversation.dateCreated);
            const now = moment();
            const diff = moment.duration(now.diff(start));

            const minutes = String(diff.minutes() + diff.hours() * 60).padStart(2, '0');
            const seconds = String(diff.seconds()).padStart(2, '0');
            setDuration(`${minutes}:${seconds}`);
        };

        updateDuration();
        const interval = setInterval(updateDuration, 1000);

        return () => clearInterval(interval);
    }, [conversation?.status, conversation?.dateCreated]);

    const vdn = conversation?.vdn || conversation?.transferNumber || null;
    const handleContextMenu = (event) => {
        event.preventDefault();
        setMenuAnchor({mouseX: event.clientX + 2, mouseY: event.clientY - 6});
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
    };

    const handleToggleResolved = () => {
        ConversationRepository.toggleResolved({id: conversation?.id})
            .then(response => onUpdateConversation(prev => response?.data || prev))
            .catch(error => console.log(error))
        handleCloseMenu();
    };

    const handleCallCustomer = async (e) => {
        e?.stopPropagation();
        e?.preventDefault();

        let agentVdn = project?.configuration?.redirect_vdn;
        if (project?.vdnManager[currentUser?.username]) {
            agentVdn = project?.vdnManager[currentUser?.username];
        }
        console.log(agentVdn)
        if (agentVdn && conversation?.sender) {
            const body = {
                username: currentUser?.username,
                vdn: agentVdn,
                phone_number: conversation?.sender,
                initiate_call_api: project?.configuration?.initiate_call_api
            };
            const params = {conversation_id: conversationId, flow_uid: project?.id};

            const response = await confirm({title: `Do you want to initiate conversation with ${body?.phone_number}`})
            if (response) {
                ConversationRepository.initiateConversation({body, params})
                    .then(response => {
                        toast.success(response?.data?.message || `Initiated conversation with ${body?.phone_number}`);
                    })
                    .catch(error => {
                        toast.error(error?.message || `Failed to initiated conversation with ${body?.phone_number}`);
                    })
            }
        }
        handleCloseMenu();
    };

    const selectedTags = project?.settings?.tags?.filter(i => conversation?.tags?.includes(i?.id));
    const type = project?.settings?.projectType || "VOICE_BOT"

    return (
        <>
            <ListItemButton
                onClick={() => navigate(`/project/${project?.id}/supervision/${conversation?.id}`)}
                onContextMenu={handleContextMenu}
                sx={{
                    bgcolor: +conversationId === conversation?.id
                        ? isResolved ? grey[300] : grey[200]
                        : isResolved ? grey[50] : "",
                    borderBottom: 1,
                    borderColor: "divider",
                    opacity: isResolved ? 0.9 : 1,
                    cursor: "pointer"
                }}
            >
                <ListItemIcon>
                    <Tooltip title={`${conversation?.id}. ${conversation?.status} ${vdn ? ` → (${vdn})` : ""}`}>
                        <Avatar
                            sx={{
                                borderRadius: 2,
                                bgcolor: type === "VOICE_BOT" || isResolved ? grey[300] : getBgColorIcon(conversation?.status),
                                color: type === "VOICE_BOT" || isResolved ? `${grey[700]} !important` : "inherit"
                            }}
                        >
                            {type !== "VOICE_BOT" ? <HugeiconsIcon icon={Robot01Icon} size={22} color={grey[700]}/> : isResolved
                                ? <HugeiconsIcon icon={CheckCheckIcon} size={22} color={grey[700]}/>
                                : getConversationIcon(conversation?.status)}
                        </Avatar>
                    </Tooltip>
                </ListItemIcon>

                <ListItemText
                    primary={
                        <Stack direction={"row"} gap={0.5} alignItems={"center"}>
                            <Tooltip title={conversation?.monitoredBy || <em>Not supervised</em>}>
                                {conversation?.monitoredBy ? <HugeiconsIcon icon={ViewIcon} size={16}/> : <HugeiconsIcon icon={EyeOffIcon} size={16}/>}
                            </Tooltip>
                            <Typography size={"inherit"} fontWeight={"inherit"} gutterBottom={false}>
                                {(() => {
                                    const senderText = project?.phonebook?.[conversation?.sender] || conversation?.sender || "";
                                    return senderText.length > 20 ? `${senderText.slice(0, 20)}...` : senderText;
                                })()} {vdn ? ` → (${vdn})` : ""}
                            </Typography>
                        </Stack>
                    }
                    secondary={
                        conversation.status === "SUCCESS" && successTag?.title ?
                            successTag?.title
                            :
                            selectedTags?.length > 0 ?
                                selectedTags?.filter(i => !!i?.title)?.map(i => i?.title)?.join(", ") : "Just started..."
                    }
                    primaryTypographyProps={{
                        fontWeight: 500,
                        color: isResolved ? grey[700] : "inherit"
                    }}
                    secondaryTypographyProps={{
                        component: 'em',
                        color: isResolved ? grey[600] : "text.secondary",
                        noWrap: true,
                        fontFamily: "Inter",
                    }}
                />

                <ListItemIcon sx={{minWidth: 0}}>
                    {
                        type === "VOICE_BOT" && conversation?.status === "ACTIVE" && !isResolved ? (
                            <Stack direction={"row"} gap={1} alignItems={"center"}>
                                <Typography fontSize={"0.9em"} fontWeight={500}>
                                    {duration}
                                </Typography>
                                <Avatar sx={{width: 25, height: 25, p: 0.2, bgcolor: green[400]}}>
                                    <HugeiconsIcon icon={PhoneCallIcon} size={14}/>
                                </Avatar>
                            </Stack>
                        ) : (
                            <Stack direction={"column"} gap={1} justifyContent={"space-between"}>

                                <Typography
                                    sx={{
                                        fontSize: "0.75em",
                                        textAlign: "center",
                                        color: isResolved ? grey[600] : grey[700]
                                    }}
                                >
                                    {
                                        moment(conversation?.dateCreated).isSame(moment(), 'day')
                                            ? moment(conversation?.dateCreated).format("HH:mm")
                                            : moment(conversation?.dateCreated).format("DD MMM HH:mm")
                                    }
                                </Typography>
                                <Box>
                                    {
                                        conversation?.comments?.length > 0 ? (
                                            <Stack direction={"row"} gap={1} alignItems={"center"}
                                                // className={"border-shadow"}
                                                   sx={{
                                                       // bgcolor: grey[200],
                                                       // px: 1, py: 0.4,
                                                       // borderRadius: 2,
                                                       color: grey[700]
                                                   }}
                                            >
                                                <HugeiconsIcon icon={Message02Icon} size={15}/>
                                                <Typography fontSize={14}
                                                            sx={{py: 0}}>{conversation?.comments?.length}</Typography>
                                            </Stack>
                                        ) : null
                                    }
                                </Box>
                            </Stack>
                        )
                    }
                </ListItemIcon>
            </ListItemButton>
            {/* Context Menu */}
            <Menu
                open={Boolean(menuAnchor)}
                onClose={handleCloseMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    menuAnchor
                        ? {top: menuAnchor.mouseY, left: menuAnchor.mouseX}
                        : undefined
                }
                MenuListProps={{sx: {borderRadius: 4, minWidth: "200px"}}}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 4,
                            minWidth: "200px",
                            p: 1,
                            py: 0,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5
                        }
                    }
                }}
                componentsProps={{
                    paper: {
                        elevation: 3,
                        sx: {minWidth: 230}
                    }
                }}
            >

                <MenuItem onClick={handleCallCustomer} sx={{borderRadius: 2}}>
                    <ListItemIcon color={"success"}><HugeiconsIcon icon={PhoneIcon} color={"success"}/></ListItemIcon>
                    <ListItemText primary={"Call Customer"}/>
                </MenuItem>
                <MenuItem onClick={handleToggleResolved} sx={{borderRadius: 2}}>
                    <ListItemIcon>{isResolved ? <HugeiconsIcon icon={Refresh01Icon} color={"error"}/> :
                        <HugeiconsIcon icon={CircleCheckIcon} color={"success"}/>}</ListItemIcon>
                    <ListItemText primary={isResolved ? "Mark as Unresolved" : "Mark as Resolved"}/>

                </MenuItem>
            </Menu>
        </>
    );
});
